import {
  ExchangeBalanceResponse,
  GetBalanceParams,
  GetPairParams,
} from 'api/types/httpsTypes/exchange-balance';
import { api } from 'helpers/api/apiCore';
import {
  GetExchangesResponse,
  GetMarketsAvailableResponse,
  GetPairsAvailableParams,
  GetPairsResponse,
  GetQuoteAssetsAvailableParams,
  QuoteAssetsResponse,
} from 'api/types/httpsTypes/exchange-info';
import { toQueryString } from '../../../modules/routing/query-string';
import { IpProxyGroupResponse } from 'api/types/httpsTypes/connected-exchanges';
import {
  SupportedPairs,
  SupportedSymbols,
} from 'api/types/httpsTypes/marketplace-performance';

function cleanSymbol(symbol: string) {
  return symbol.replace('/', '');
}

const moduleKey = 'exchange';

export const exchangeKeys = {
  getBalance: (params: GetBalanceParams) => [
    moduleKey,
    params.exchangeKeyId,
    params.market,
    params.assetTicker,
  ],
  getPairs: (params: GetPairParams) => [moduleKey, 'pairs', params],
  getBaseSymbols: (params: GetPairParams) => [
    moduleKey,
    'base-symbols',
    params,
  ],
  getPairsAvailable: (params: GetPairsAvailableParams) => [
    moduleKey,
    'pairs-available',
    params,
  ],
  getQuoteAssetsAvailable: (params: GetQuoteAssetsAvailableParams) => [
    moduleKey,
    'quote-assets',
    params,
  ],
  getPair: (id: number) => [moduleKey, 'pairs', id],
  getExchanges: () => [moduleKey],
  getIpProxyGroup: (exchangeId: number) => [
    moduleKey,
    exchangeId,
    'proxy-group',
  ],
  getMarketsAvailable: () => [moduleKey, 'markets-available'],
};

export const ExchangeService = {
  async getIpProxyGroup(exchangeId: number) {
    const response = await api.get<IpProxyGroupResponse>(
      `/api/v2/exchange/${exchangeId}/proxy-group`
    );

    return response.data;
  },
  getBalance: async ({
    exchangeKeyId,
    assetTicker,
    market,
  }: GetBalanceParams) => {
    const response = await api.get<ExchangeBalanceResponse>(
      `/api/v2/exchange/free-balance/exchange-key-id/${exchangeKeyId}/markets/${market}/asset/${assetTicker}`
    );
    return response.data.data;
  },
  getPair: async (id: number) => {
    const response = await api.get<GetPairsResponse>(
      `/api/v2/exchange_info/symbols/?${toQueryString({
        id,
      })}`
    );
    return response.data.data[0];
  },
  getPairs: async ({ symbol }: GetPairParams) => {
    const response = await api.get<GetPairsResponse>(
      `/api/v2/exchange_info/symbols/?${toQueryString({
        ...(symbol && { symbol__ilike: `%${cleanSymbol(symbol)}%` }),
      })}`
    );
    return response.data.data;
  },
  getBaseSymbols: async ({ symbol, limit }: GetPairParams) => {
    const response = await api.get<GetPairsResponse>(
      `/api/v2/exchange_info/symbols/?${toQueryString({
        limit,
        ...(symbol && { symbol__ilike: `${cleanSymbol(symbol)}%` }),
      })}`
    );
    return response.data.data;
  },
  getPairsAvailable: async ({
    exchangesId,
    marketName,
    quoteAssetId,
  }: GetPairsAvailableParams) => {
    const response = await api.get<any[]>(
      `/api/v2/pairs-available/?${toQueryString({
        exchanges_id: JSON.stringify(exchangesId),
        market_name: marketName,
        quote_asset_id: quoteAssetId,
      })}`
    );
    return Object.values(response.data);
  },

  getPairsAvailableV2: async ({
    exchangesId,
    marketName,
    quoteAssetId,
  }: GetPairsAvailableParams) => {
    const response = await api.get<SupportedPairs['pairs']>(
      `/api/v2/pairs-available/?${toQueryString({
        exchanges_id: JSON.stringify(exchangesId),
        market_name: marketName,
        quote_asset_id: quoteAssetId,
      })}`
    );

    return Object.entries(response.data).map(([id, pair]) => ({
      symbolId: +id,
      quoteAsset: pair.quote,
      baseAsset: pair.base,
    })) as SupportedSymbols['symbols'];
  },
  getQuoteAssetsAvailable: async ({
    exchangesId,
    marketName,
  }: GetQuoteAssetsAvailableParams) => {
    const response = await api.get<QuoteAssetsResponse>(
      `/api/quote-assets-available?${toQueryString({
        exchanges_id: JSON.stringify(exchangesId),
        market_name: marketName,
      })}`
    );
    return Object.entries(response.data).map(([id, name]) => ({
      id: +id,
      name,
    }));
  },
  getExchanges: async () => {
    const response = await api.get<GetExchangesResponse>(
      `/api/v2/exchange_info/exchanges?isActive=true`
    );
    return response.data.data.filter((e) => e.key !== 'binance_paper_trading');
  },
  getMarketsAvailable: async () => {
    const response = await api.get<GetMarketsAvailableResponse>(
      '/api/markets-available'
    );
    return response.data;
  },
};
